import type {
  PingResult,
  PingSummary,
  SpeedResult,
  SpeedSample,
  ChunkInfo,
  ChunkAnalysis,
  LatencyMeasurement,
  ClientInfo,
  TraceResult,
  StreamDiagnostics,
  FlowPoint,
  QualityMetrics,
  DiagnosticResult,
  TestPhase,
} from '../types/diagnostics';
import {
  computeStats,
  computeJitter,
  computeMOS,
  computeFlowStability,
  detectCongestion,
  detectISPShaping,
  computeBufferHealth,
  getGrade,
} from '../utils/statistics';

const API_BASE = '';

type ProgressCallback = (result: Partial<DiagnosticResult>) => void;

/**
 * Main network diagnostic test orchestrator
 */
export class NetworkTester {
  private abortController: AbortController | null = null;
  private onProgress: ProgressCallback;

  constructor(onProgress: ProgressCallback) {
    this.onProgress = onProgress;
  }

  abort(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  async runFullDiagnostic(): Promise<DiagnosticResult> {
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const result: DiagnosticResult = {
      timestamp: Date.now(),
      clientInfo: null,
      ping: null,
      latencies: [],
      download: null,
      upload: null,
      chunkAnalysis: null,
      streamDiagnostics: null,
      quality: null,
      trace: null,
      phase: 'connecting',
      progress: 0,
      error: null,
    };

    try {
      // Phase 1: Get client info
      this.updatePhase(result, 'info', 5);
      result.clientInfo = await this.getClientInfo(signal);

      // Phase 2: Latency tests
      this.updatePhase(result, 'latency', 10);
      result.latencies = await this.measureLatencies(signal);
      result.ping = await this.runPingTest(signal, 30);
      this.updatePhase(result, 'latency', 35);

      // Phase 3: Download test
      this.updatePhase(result, 'download', 40);
      const downloadResult = await this.runDownloadTest(signal);
      result.download = downloadResult.speed;
      result.chunkAnalysis = downloadResult.chunks;
      result.streamDiagnostics = downloadResult.stream;

      // Phase 4: Upload test
      this.updatePhase(result, 'upload', 70);
      result.upload = await this.runUploadTest(signal);

      // Phase 5: Analysis
      this.updatePhase(result, 'analysis', 90);
      result.quality = this.computeQuality(result);

      // Phase 6: Trace (best-effort)
      try {
        result.trace = await this.getTrace(signal);
      } catch {
        // Trace is optional
      }

      // Save results
      try {
        const saved = await this.saveResults(result, signal);
        result.id = saved.id;
      } catch {
        // Saving is optional
      }

      this.updatePhase(result, 'complete', 100);
      return result;
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        result.phase = 'idle';
        result.error = 'Test aborted';
      } else {
        result.phase = 'error';
        result.error = err instanceof Error ? err.message : 'Unknown error';
      }
      this.onProgress(result);
      return result;
    }
  }

  private updatePhase(result: DiagnosticResult, phase: TestPhase, progress: number): void {
    result.phase = phase;
    result.progress = progress;
    this.onProgress({ ...result });
  }

  // ---- Client Info ----

  async getClientInfo(signal: AbortSignal): Promise<ClientInfo> {
    const resp = await fetch(`${API_BASE}/api/info`, { signal });
    return resp.json();
  }

  // ---- Latency Measurements ----

  async measureLatencies(signal: AbortSignal): Promise<LatencyMeasurement[]> {
    const latencies: LatencyMeasurement[] = [];

    // HTTP latency
    const httpLatency = await this.measureHTTPLatency(signal);
    latencies.push({ type: 'http', durationMs: httpLatency, timestamp: Date.now() });

    // DNS resolve time via resource timing
    const dnsTime = this.measureDNSFromTiming();
    if (dnsTime > 0) {
      latencies.push({ type: 'dns', durationMs: dnsTime, timestamp: Date.now() });
    }

    // TCP handshake latency
    const tcpLatency = await this.measureTCPHandshake(signal);
    latencies.push({ type: 'tcp', durationMs: tcpLatency, timestamp: Date.now() });

    // TLS handshake (from resource timing)
    const tlsTime = this.measureTLSFromTiming();
    if (tlsTime > 0) {
      latencies.push({ type: 'tls', durationMs: tlsTime, timestamp: Date.now() });
    }

    // WebSocket latency
    try {
      const wsLatency = await this.measureWSLatency();
      latencies.push({ type: 'ws', durationMs: wsLatency, timestamp: Date.now() });
    } catch {
      // WS measurement optional
    }

    // WebRTC ICE candidate latency
    try {
      const rtcLatency = await this.measureWebRTCLatency();
      latencies.push({ type: 'webrtc', durationMs: rtcLatency, timestamp: Date.now() });
    } catch {
      // WebRTC optional
    }

    return latencies;
  }

  private async measureHTTPLatency(signal: AbortSignal): Promise<number> {
    const start = performance.now();
    await fetch(`${API_BASE}/api/ping?seq=0&t=${Date.now()}`, {
      signal,
      cache: 'no-store',
    });
    return Math.round((performance.now() - start) * 100) / 100;
  }

  private async measureTCPHandshake(signal: AbortSignal): Promise<number> {
    const start = performance.now();
    await fetch(`${API_BASE}/api/tcp-handshake`, { signal, cache: 'no-store' });
    return Math.round((performance.now() - start) * 100) / 100;
  }

  private measureDNSFromTiming(): number {
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const recent = entries.filter(
      (e) => e.name.includes('/api/') && e.domainLookupEnd > 0 && e.domainLookupStart > 0
    );
    if (recent.length === 0) return 0;
    const last = recent[recent.length - 1];
    return Math.round((last.domainLookupEnd - last.domainLookupStart) * 100) / 100;
  }

  private measureTLSFromTiming(): number {
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const recent = entries.filter(
      (e) => e.name.includes('/api/') && e.secureConnectionStart > 0 && e.connectEnd > 0
    );
    if (recent.length === 0) return 0;
    const last = recent[recent.length - 1];
    return Math.round((last.connectEnd - last.secureConnectionStart) * 100) / 100;
  }

  private measureWSLatency(): Promise<number> {
    return new Promise((resolve, reject) => {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${proto}//${location.host}/ws/ping`);
      const start = performance.now();

      const timeout = setTimeout(() => {
        ws.close();
        reject(new Error('WS timeout'));
      }, 5000);

      ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'ping', seq: 0, timestamp: Date.now() }));
      };

      ws.onmessage = () => {
        const latency = performance.now() - start;
        clearTimeout(timeout);
        ws.close();
        resolve(Math.round(latency * 100) / 100);
      };

      ws.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('WS error'));
      };
    });
  }

  private measureWebRTCLatency(): Promise<number> {
    return new Promise((resolve, reject) => {
      const start = performance.now();
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });

      const timeout = setTimeout(() => {
        pc.close();
        reject(new Error('WebRTC timeout'));
      }, 5000);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const latency = performance.now() - start;
          clearTimeout(timeout);
          pc.close();
          resolve(Math.round(latency * 100) / 100);
        }
      };

      pc.onicecandidateerror = () => {
        clearTimeout(timeout);
        pc.close();
        reject(new Error('ICE error'));
      };

      pc.createDataChannel('latency');
      pc.createOffer().then((offer) => pc.setLocalDescription(offer)).catch(reject);
    });
  }

  // ---- Ping Test ----

  async runPingTest(signal: AbortSignal, count = 30): Promise<PingSummary> {
    const results: PingResult[] = [];

    for (let i = 0; i < count; i++) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const start = performance.now();
      try {
        await fetch(`${API_BASE}/api/ping?seq=${i}&t=${Date.now()}`, {
          signal,
          cache: 'no-store',
        });
        const rtt = performance.now() - start;
        results.push({ seq: i, rttMs: rtt, lost: false, timestamp: Date.now() });
      } catch {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        results.push({ seq: i, rttMs: 0, lost: true, timestamp: Date.now() });
      }

      // Emit progress
      this.onProgress({
        ping: this.summarizePing(results),
        progress: 10 + (25 * (i + 1)) / count,
      });

      await this.delay(200);
    }

    return this.summarizePing(results);
  }

  private summarizePing(results: PingResult[]): PingSummary {
    const received = results.filter((r) => !r.lost);
    const rtts = received.map((r) => r.rttMs);
    const stats = computeStats(rtts);

    return {
      min: stats.min,
      max: stats.max,
      avg: stats.avg,
      p50: stats.p50,
      p90: stats.p90,
      p99: stats.p99,
      jitter: computeJitter(rtts),
      lossPercent: Math.round(((results.length - received.length) / results.length) * 10000) / 100,
      sent: results.length,
      received: received.length,
      rtts,
    };
  }

  // ---- Download Test ----

  async runDownloadTest(signal: AbortSignal): Promise<{
    speed: SpeedResult;
    chunks: ChunkAnalysis;
    stream: StreamDiagnostics;
  }> {
    const samples: SpeedSample[] = [];
    const chunks: ChunkInfo[] = [];
    const flowPoints: FlowPoint[] = [];

    const testStart = performance.now();
    const downloadSize = 25 * 1024 * 1024; // 25MB
    let totalBytes = 0;
    let chunkIndex = 0;
    let lastChunkTime = testStart;

    const response = await fetch(
      `${API_BASE}/api/download?size=${downloadSize}&chunkSize=65536`,
      { signal, cache: 'no-store' }
    );

    const reader = response.body!.getReader();
    const startTime = performance.now();

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const now = performance.now();
      const chunkSize = value.byteLength;
      totalBytes += chunkSize;

      const gap = now - lastChunkTime;
      const elapsed = now - startTime;
      const currentSpeed = (totalBytes * 8) / (elapsed / 1000) / 1_000_000;

      chunks.push({
        index: chunkIndex,
        size: chunkSize,
        receivedAt: now,
        duration: gap,
        gap: chunkIndex > 0 ? gap : 0,
        outOfOrder: false, // Sequential in HTTP
      });

      if (elapsed > 500) {
        // Record samples every 500ms
        const lastSample = samples.length > 0 ? samples[samples.length - 1] : null;
        if (!lastSample || now - lastSample.timestamp > 500) {
          samples.push({
            timestamp: now,
            bytes: totalBytes,
            speedMbps: currentSpeed,
            elapsed,
          });

          flowPoints.push({
            timestamp: now,
            throughputMbps: currentSpeed,
            rttMs: gap,
            windowSize: chunkSize,
          });

          this.onProgress({
            download: {
              speedMbps: currentSpeed,
              totalBytes,
              durationMs: elapsed,
              samples,
              burstMbps: Math.max(...samples.map((s) => s.speedMbps)),
              sustainedMbps: currentSpeed,
            },
            progress: 40 + (30 * totalBytes) / downloadSize,
          });
        }
      }

      lastChunkTime = now;
      chunkIndex++;
    }

    const totalDuration = performance.now() - startTime;
    const finalSpeed = (totalBytes * 8) / (totalDuration / 1000) / 1_000_000;
    const throughputs = samples.map((s) => s.speedMbps);

    // Detect ramp-up
    let rampUpTime = 0;
    const peakSpeed = Math.max(...throughputs, finalSpeed);
    for (const s of samples) {
      if (s.speedMbps >= peakSpeed * 0.9) {
        rampUpTime = s.elapsed;
        break;
      }
    }

    // Detect slow start
    let slowStartPhase = 0;
    for (const s of samples) {
      if (s.speedMbps >= peakSpeed * 0.5) {
        slowStartPhase = s.elapsed;
        break;
      }
    }

    const chunkGaps = chunks.filter((c) => c.gap > 0).map((c) => c.gap);
    const bufferUnderruns = chunkGaps.filter((g) => g > 100).length; // > 100ms gaps

    return {
      speed: {
        speedMbps: Math.round(finalSpeed * 100) / 100,
        totalBytes,
        durationMs: totalDuration,
        samples,
        burstMbps: Math.round(Math.max(...throughputs, finalSpeed) * 100) / 100,
        sustainedMbps: Math.round(finalSpeed * 100) / 100,
      },
      chunks: {
        chunks,
        avgSize: chunks.length > 0 ? Math.round(totalBytes / chunks.length) : 0,
        avgGap:
          chunkGaps.length > 0 ? Math.round((chunkGaps.reduce((a, b) => a + b, 0) / chunkGaps.length) * 100) / 100 : 0,
        outOfOrderCount: 0,
        bufferUnderruns,
        totalBytes,
      },
      stream: {
        flowPoints,
        congestionDetected: detectCongestion(chunkGaps, throughputs) > 0.5,
        congestionProbability: detectCongestion(chunkGaps, throughputs),
        rampUpTimeMs: rampUpTime,
        slowStartPhaseMs: slowStartPhase,
        retransmissionEstimate: bufferUnderruns / Math.max(chunks.length, 1),
        holBlockingDetected: chunkGaps.some((g) => g > 500),
        ispShapingDetected: detectISPShaping(throughputs),
        flowStability: computeFlowStability(throughputs),
      },
    };
  }

  // ---- Upload Test ----

  async runUploadTest(signal: AbortSignal): Promise<SpeedResult> {
    const uploadSize = 10 * 1024 * 1024; // 10MB
    const chunkSize = 256 * 1024; // 256KB chunks
    const data = new ArrayBuffer(uploadSize);
    const samples: SpeedSample[] = [];

    // Fill with random data (getRandomValues max 65536 bytes per call)
    const view = new Uint8Array(data);
    for (let offset = 0; offset < view.length; offset += 65536) {
      const end = Math.min(offset + 65536, view.length);
      crypto.getRandomValues(view.subarray(offset, end));
    }

    const startTime = performance.now();
    let uploaded = 0;

    // Upload in chunks using fetch
    while (uploaded < uploadSize) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const end = Math.min(uploaded + chunkSize, uploadSize);
      const chunk = data.slice(uploaded, end);

      await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: chunk,
        signal,
        headers: { 'Content-Type': 'application/octet-stream' },
      });

      uploaded = end;
      const elapsed = performance.now() - startTime;
      const currentSpeed = (uploaded * 8) / (elapsed / 1000) / 1_000_000;

      samples.push({
        timestamp: performance.now(),
        bytes: uploaded,
        speedMbps: currentSpeed,
        elapsed,
      });

      this.onProgress({
        upload: {
          speedMbps: currentSpeed,
          totalBytes: uploaded,
          durationMs: elapsed,
          samples,
          burstMbps: Math.max(...samples.map((s) => s.speedMbps)),
          sustainedMbps: currentSpeed,
        },
        progress: 70 + (20 * uploaded) / uploadSize,
      });
    }

    const totalDuration = performance.now() - startTime;
    const finalSpeed = (uploadSize * 8) / (totalDuration / 1000) / 1_000_000;

    return {
      speedMbps: Math.round(finalSpeed * 100) / 100,
      totalBytes: uploadSize,
      durationMs: totalDuration,
      samples,
      burstMbps: Math.round(Math.max(...samples.map((s) => s.speedMbps)) * 100) / 100,
      sustainedMbps: Math.round(finalSpeed * 100) / 100,
    };
  }

  // ---- Quality Computation ----

  computeQuality(result: DiagnosticResult): QualityMetrics {
    const avgLatency = result.ping?.avg ?? 50;
    const jitter = result.ping?.jitter ?? 5;
    const loss = result.ping?.lossPercent ?? 0;

    const mos = computeMOS(avgLatency, jitter, loss);

    const downloadSamples = result.download?.samples.map((s) => s.speedMbps) ?? [];
    const chunkGaps =
      result.chunkAnalysis?.chunks.filter((c) => c.gap > 0).map((c) => c.gap) ?? [];

    return {
      mosScore: mos,
      flowStability: computeFlowStability(downloadSamples),
      bufferHealth: computeBufferHealth(
        result.chunkAnalysis?.chunks.map((c) => ({ gap: c.gap, duration: c.duration })) ?? []
      ),
      congestionProbability: detectCongestion(
        result.ping?.rtts ?? [],
        downloadSamples
      ),
      ispShapingDetected: detectISPShaping(downloadSamples),
      natType: 'unknown',
      ipVersion: result.clientInfo?.ipv6 ? 'IPv6' : 'IPv4',
      overallGrade: getGrade(mos),
    };
  }

  // ---- Trace ----

  async getTrace(signal: AbortSignal): Promise<TraceResult> {
    const resp = await fetch(`${API_BASE}/api/trace`, { signal });
    return resp.json();
  }

  // ---- Save Results ----

  async saveResults(
    result: DiagnosticResult,
    signal: AbortSignal
  ): Promise<{ id: string; link: string }> {
    const resp = await fetch(`${API_BASE}/api/results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(result),
      signal,
    });
    return resp.json();
  }

  // ---- Export ----

  static exportJSON(result: DiagnosticResult): string {
    return JSON.stringify(result, null, 2);
  }

  static downloadJSON(result: DiagnosticResult): void {
    const json = NetworkTester.exportJSON(result);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `netdiag-${new Date().toISOString().slice(0, 19)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
